import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  TireRepair as TireRepairIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useUserStore } from 'store';
import DataTable from '../shared/DataTable';

const getStatusColor = (status) => {
  switch (status) {
    case 'stored': return 'success';
    case 'on_vehicle': return 'info';
    default: return 'default';
  }
};

const getTreadDepthColor = (depth) => {
  const d = parseFloat(depth);
  if (isNaN(d)) return 'text.secondary';
  if (d < 3) return 'error.main';
  if (d < 4) return 'warning.main';
  return 'success.main';
};

function SalesPipelinePage({ t }) {
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFlagged = useCallback(async () => {
    if (!currentOrganization?.organization_id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/tire-hotel-cards/flagged/', {
        headers: { 'X-Organization-Id': currentOrganization.organization_id },
      });
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch flagged cards:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.organization_id]);

  useEffect(() => {
    fetchFlagged();
  }, [fetchFlagged]);

  const handleRowClick = (item) => {
    navigate(`/tire-hotel/${item.id}`);
  };

  const columns = [
    {
      key: 'customer',
      label: t('tire_hotel.customer'),
      maxWidth: 200,
      render: (item) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {item.customer_name}
          </Typography>
          {item.customer_email && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {item.customer_email}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'vehicle',
      label: t('tire_hotel.vehicle'),
      noWrap: true,
      render: (item) => item.vehicle_registration_number,
    },
    {
      key: 'tire_size',
      label: t('tire_hotel.tire_size'),
      noWrap: true,
      render: (item) => item.tire_size_display,
    },
    {
      key: 'type',
      label: t('tire_hotel.tire_set_type'),
      render: (item) => (
        <Chip
          label={t(`tire_hotel.${item.tire_set_type}`)}
          size="small"
          color={item.tire_set_type === 'winter' ? 'info' : item.tire_set_type === 'summer' ? 'warning' : 'default'}
        />
      ),
    },
    {
      key: 'min_tread_depth',
      label: t('tire_hotel.min_tread_depth'),
      render: (item) => (
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ color: getTreadDepthColor(item.min_tread_depth) }}
        >
          {item.min_tread_depth ? `${item.min_tread_depth} mm` : '-'}
        </Typography>
      ),
    },
    {
      key: 'dot_code',
      label: t('tire_hotel.dot_code'),
      noWrap: true,
      render: (item) => item.dot_code || '-',
    },
    {
      // A flagged set can be in the hotel or on the car: the pipeline lists both
      key: 'status',
      label: t('tire_hotel.status'),
      width: '1%',
      render: (item) => (
        <Chip size="small" label={t(`tire_hotel.${item.status}`)} color={getStatusColor(item.status)} sx={{ fontSize: '11px' }} />
      ),
    },
    {
      key: 'replacement_reason',
      label: t('tire_hotel.replacement_reason'),
      maxWidth: 220,
      render: (item) => item.replacement_reason || '-',
    },
    {
      key: 'phone',
      label: t('Phone'),
      noWrap: true,
      render: (item) => item.customer_phone || '-',
    },
  ];

  // Mobile card view
  const renderMobileCards = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {items.map((item) => (
        <Paper
          key={item.id}
          onClick={() => handleRowClick(item)}
          sx={{
            p: 2,
            borderRadius: '12px',
            cursor: 'pointer',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body1" fontWeight={600} noWrap>
                {item.customer_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.vehicle_registration_number} &middot; {item.tire_size_display}
              </Typography>
            </Box>
            <Chip
              label={item.min_tread_depth ? `${item.min_tread_depth} mm` : '-'}
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor: getTreadDepthColor(item.min_tread_depth),
                color: 'white',
                ml: 1,
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              label={t(`tire_hotel.${item.tire_set_type}`)}
              size="small"
              color={item.tire_set_type === 'winter' ? 'info' : item.tire_set_type === 'summer' ? 'warning' : 'default'}
            />
            <Chip size="small" label={t(`tire_hotel.${item.status}`)} color={getStatusColor(item.status)} />
            {item.dot_code && (
              <Typography variant="caption" color="text.secondary">
                DOT: {item.dot_code}
              </Typography>
            )}
          </Box>
          {item.replacement_reason && (
            <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.5 }}>
              {item.replacement_reason}
            </Typography>
          )}
          {(item.customer_phone || item.customer_email) && (
            <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
              {item.customer_phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">{item.customer_phone}</Typography>
                </Box>
              )}
              {item.customer_email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">{item.customer_email}</Typography>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      ))}
    </Box>
  );

  return (
    <Box data-testid="sales-pipeline-page">
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            component="h4"
            variant="h4"
            data-testid="pipeline-heading"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('tire_hotel.sales_pipeline')}
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: 'text.secondary',
              mt: 0.5,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {t('tire_hotel.pipeline_subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Summary card */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 150, flex: 1, maxWidth: 250 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body2" color="text.secondary">
              {t('tire_hotel.flagged_count')}
            </Typography>
            <Typography variant="h5" sx={{ color: items.length > 0 ? 'warning.dark' : 'text.primary' }}>
              {loading ? '-' : items.length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Desktop Table */}
      <DataTable
        columns={columns}
        data={items}
        loading={loading}
        onRowClick={handleRowClick}
        emptyIcon={<TireRepairIcon />}
        emptyTitle={t('tire_hotel.no_flagged_cards')}
        emptyDescription={t('tire_hotel.no_flagged_subtitle')}
        testIdPrefix="pipeline-card"
      />

      {/* Mobile Cards */}
      <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !error && items.length > 0 ? (
          renderMobileCards()
        ) : null}
      </Box>
    </Box>
  );
}

export default withTranslation()(SalesPipelinePage);
